import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { BRAND_NAME, BRAND_TITLE, BRAND_SUB, BRAND_BADGE } from "@/lib/brand";
import { isClientRole } from "@/pages/SupportPortal";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome to ${BRAND_NAME}`);
      nav(isClientRole(u?.role) ? "/support" : "/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-md bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center font-mono font-bold text-white text-base">{BRAND_BADGE}</div>
          <div>
            <div className="font-display font-bold text-lg tracking-tight leading-tight">{BRAND_TITLE}</div>
            <div className="text-[13px] text-zinc-400 leading-tight">{BRAND_SUB}</div>
          </div>
        </div>

        <h1 className="font-display text-2xl font-semibold tracking-tight">Sign in</h1>
        <p className="text-sm text-zinc-500 mt-1 mb-6">Sign in to continue to {BRAND_NAME}.</p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Email</label>
            <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="you@company.com" className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Password</label>
            <input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required placeholder="Password" className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
          </div>
          <button data-testid="login-submit" type="submit" disabled={loading} className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md px-4 py-2 transition-colors disabled:opacity-60">
            {loading ? "Signing in…" : "Sign in"} <ArrowRight size={14} />
          </button>
        </form>

        <p className="text-xs text-zinc-500 mt-6">No account? <Link to="/register" className="text-blue-400 hover:underline" data-testid="register-link">Request access</Link></p>
      </div>
    </div>
  );
}
